import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart3, Loader2, Target, FileText, CalendarClock, Users, TrendingUp, CheckCircle2,
  XCircle, Clock, AlertOctagon, Layers, ArrowRight, Building2, Factory,
} from "lucide-react";
import { eur } from "@/lib/crm";

const PERIODS = [
  { k: "today", label: "Oggi" }, { k: "week", label: "Questa settimana" }, { k: "month", label: "Questo mese" },
  { k: "prevmonth", label: "Mese precedente" }, { k: "30d", label: "Ultimi 30 giorni" },
  { k: "quarter", label: "Questo trimestre" }, { k: "year", label: "Quest'anno" }, { k: "custom", label: "Personalizzato" },
];

function rangeFor(k) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
  const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  if (k === "today") return [iso(now), iso(now)];
  if (k === "week") { const wd = (now.getDay() + 6) % 7; const s = new Date(y, m, d - wd); return [iso(s), iso(now)]; }
  if (k === "month") return [iso(new Date(y, m, 1)), iso(now)];
  if (k === "prevmonth") return [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))];
  if (k === "30d") return [iso(new Date(y, m, d - 30)), iso(now)];
  if (k === "quarter") { const qs = Math.floor(m / 3) * 3; return [iso(new Date(y, qs, 1)), iso(now)]; }
  if (k === "year") return [iso(new Date(y, 0, 1)), iso(now)];
  return ["", ""];
}

function Kpi({ icon: Icon, label, value, sub, tone = "bg-brand/10 text-brand", onClick, testid }) {
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testid}
      className={`ds-card p-4 text-left transition-shadow ${onClick ? "hover:shadow-md cursor-pointer" : "cursor-default"}`}>
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tone}`}><Icon className="h-5 w-5" /></div>
        <div><div className="font-display text-2xl font-bold text-slate-900">{value}</div><div className="text-xs text-slate-500">{label}</div></div>
      </div>
      {sub && <div className="mt-2 text-sm font-medium text-slate-600">{sub}</div>}
    </button>
  );
}

function Bar({ label, value, max, tone = "bg-brand" }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-slate-600"><span className="truncate">{label}</span><span className="font-semibold">{value}</span></div>
      <div className="h-2 w-full rounded-full bg-slate-100"><div className={`h-2 rounded-full ${tone}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function Conv({ label, c }) {
  return (
    <div className="rounded-lg border border-border p-3" data-testid={`conv-${label}`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="font-display text-xl font-bold text-brand">{c.rate}%</span>
        <span className="text-xs text-slate-400">{c.num} / {c.den}</span>
      </div>
    </div>
  );
}

export default function DashboardDirezionePage() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [offices, setOffices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [period, setPeriod] = useState("month");
  const [from, setFrom] = useState(() => rangeFor("month")[0]);
  const [to, setTo] = useState(() => rangeFor("month")[1]);
  const [officeId, setOfficeId] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [opSort, setOpSort] = useState({ col: "quotes", dir: "desc" });

  useEffect(() => {
    api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {});
    api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {});
  }, []);

  const onPeriod = (k) => { setPeriod(k); if (k !== "custom") { const [a, b] = rangeFor(k); setFrom(a); setTo(b); } };

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      if (officeId) params.office_id = officeId;
      if (operatorId) params.operator_id = operatorId;
      const { data } = await api.get("/dashboard/management", { params });
      setD(data);
    } catch (e) { setError("Impossibile caricare la dashboard direzione."); setD(null); } finally { setLoading(false); }
  }, [from, to, officeId, operatorId]);
  useEffect(() => { load(); }, [load]);

  const drill = (path, extra = {}) => {
    const p = new URLSearchParams(extra); const qs = p.toString();
    navigate(qs ? `${path}?${qs}` : path);
  };

  const sortedOps = useMemo(() => {
    if (!d) return [];
    const arr = [...d.per_operator];
    arr.sort((a, b) => { const x = a[opSort.col], y = b[opSort.col]; const r = typeof x === "string" ? String(x).localeCompare(String(y)) : (x - y); return opSort.dir === "asc" ? r : -r; });
    return arr;
  }, [d, opSort]);
  const setSort = (col) => setOpSort((s) => ({ col, dir: s.col === col && s.dir === "desc" ? "asc" : "desc" }));

  const k = d?.kpi || {};
  const maxTs = d ? Math.max(1, ...d.timeseries.points.flatMap((p) => [p.leads, p.appointments, p.quotes])) : 1;
  const maxStage = d ? Math.max(1, ...Object.values(d.pipeline.by_stage).map((v) => v.count)) : 1;

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="dashboard-direzione">
      <div>
        <h1 className="ds-page-title flex items-center gap-2"><BarChart3 className="h-6 w-6 text-brand" /> Dashboard Direzione</h1>
        <p className="mt-1 text-sm text-slate-500">Come sta andando l'attività commerciale. Filtra per periodo, sede e operatore.</p>
      </div>

      {/* RIGA 1 — filtri globali */}
      <div className="ds-card flex flex-wrap items-end gap-3 p-4" data-testid="mgmt-filters">
        <Select value={period} onValueChange={onPeriod}>
          <SelectTrigger className="w-[180px]" data-testid="mgmt-period"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{PERIODS.map((p) => <SelectItem key={p.k} value={p.k}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
        {period === "custom" && (<>
          <div className="space-y-1"><label className="ds-label">Dal</label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="mgmt-from" /></div>
          <div className="space-y-1"><label className="ds-label">Al</label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="mgmt-to" /></div>
        </>)}
        <Select value={officeId || "all"} onValueChange={(v) => setOfficeId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[150px]" data-testid="mgmt-office"><SelectValue placeholder="Sede" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutte le sedi</SelectItem>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={operatorId || "all"} onValueChange={(v) => setOperatorId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="mgmt-operator"><SelectValue placeholder="Operatore" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti</SelectItem>{operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
        </Select>
        {d && <span className="ml-auto text-xs text-slate-400">{d.period.from} → {d.period.to}</span>}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
      ) : error || !d ? (
        <div className="ds-card flex flex-col items-center gap-3 py-12 text-center" data-testid="mgmt-error">
          <AlertOctagon className="h-8 w-8 text-danger" /><p className="text-sm text-slate-600">{error || "Nessun dato."}</p>
          <Button variant="outline" onClick={load}>Riprova</Button>
        </div>
      ) : (
        <>
          {/* RIGA 2 — KPI */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
            <Kpi testid="mgmt-kpi-leads" icon={Users} label="Lead acquisiti" value={k.leads} sub={`Lavorati: ${k.leads_worked}`} onClick={() => drill("/clienti")} />
            <Kpi testid="mgmt-kpi-appointments" icon={CalendarClock} label="Appuntamenti" value={k.appointments} onClick={() => drill("/appuntamenti")} />
            <Kpi testid="mgmt-kpi-opps" icon={Target} label="Opportunità aperte" value={k.opportunities_open} sub={eur(k.pipeline_value)} onClick={() => drill("/opportunita")} />
            <Kpi testid="mgmt-kpi-quotes" icon={FileText} label="Preventivi creati" value={k.quotes_created} sub={eur(k.quotes_value)} onClick={() => drill("/preventivi")} />
            <Kpi testid="mgmt-kpi-accepted" icon={CheckCircle2} label="Prev. accettati" value={k.quotes_accepted} sub={eur(k.accepted_value)} tone="bg-success/10 text-success" onClick={() => drill("/preventivi", { status: "Accettato" })} />
            <Kpi testid="mgmt-kpi-pending" icon={Clock} label="Prev. in attesa" value={k.quotes_pending} sub={eur(k.pending_value)} tone="bg-warning/10 text-warning" onClick={() => drill("/preventivi", { status: "Inviato" })} />
            <Kpi testid="mgmt-kpi-sent" icon={ArrowRight} label="Prev. inviati" value={k.quotes_sent} onClick={() => drill("/preventivi")} />
            <Kpi testid="mgmt-kpi-rejected" icon={XCircle} label="Prev. persi" value={k.quotes_rejected} tone="bg-danger/10 text-danger" onClick={() => drill("/preventivi", { status: "Rifiutato" })} />
            <Kpi testid="mgmt-kpi-overdue" icon={AlertOctagon} label="Attività scadute" value={k.activities_overdue} tone="bg-danger/10 text-danger" onClick={() => drill("/attivita", { overdue: "1" })} />
            <Kpi testid="mgmt-kpi-fu-overdue" icon={Clock} label="Follow-up scaduti" value={k.followups_overdue} tone="bg-warning/10 text-warning" onClick={() => drill("/attivita", { overdue: "1", type: "Follow-up preventivo" })} />
          </div>

          {/* RIGA 3 — funnel + andamento */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="ds-card p-4" data-testid="mgmt-funnel">
              <h3 className="ds-section-title mb-3 flex items-center gap-2"><Layers className="h-4 w-4 text-brand" /> Funnel commerciale</h3>
              <div className="space-y-2">
                {d.funnel.map((f) => <Bar key={f.stage} label={f.stage} value={f.value} max={Math.max(1, ...d.funnel.map((x) => x.value))} />)}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Conv label="Lead→Appuntam." c={d.conversions.lead_to_appointment} />
                <Conv label="Opp→Preventivo" c={d.conversions.opportunity_to_quote} />
                <Conv label="Preventivo→Accett." c={d.conversions.quote_to_accepted} />
                <Conv label="Win rate opp." c={d.conversions.opportunity_win_rate} />
              </div>
            </div>
            <div className="ds-card p-4" data-testid="mgmt-timeseries">
              <h3 className="ds-section-title mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-brand" /> Andamento ({d.timeseries.bucket === "day" ? "giornaliero" : "mensile"})</h3>
              {d.timeseries.points.length === 0 ? <div className="py-8 text-center text-sm text-slate-500">Nessun dato nel periodo.</div>
                : <div className="space-y-3">
                  {d.timeseries.points.slice(-8).map((p) => (
                    <div key={p.period} className="space-y-1.5">
                      <div className="text-xs font-medium text-slate-500">{p.period}</div>
                      <Bar label="Lead" value={p.leads} max={maxTs} tone="bg-brand" />
                      <Bar label="Appuntamenti" value={p.appointments} max={maxTs} tone="bg-success" />
                      <Bar label="Preventivi" value={p.quotes} max={maxTs} tone="bg-warning" />
                    </div>
                  ))}
                </div>}
            </div>
          </div>

          {/* RIGA 4 — pipeline + analisi preventivi */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="ds-card p-4" data-testid="mgmt-pipeline">
              <h3 className="ds-section-title mb-3 flex items-center gap-2"><Target className="h-4 w-4 text-brand" /> Pipeline commerciale</h3>
              <div className="space-y-2">
                {d.pipeline.stages.map((s) => (
                  <Bar key={s} label={`${s} ${d.pipeline.by_stage[s].value ? "· " + eur(d.pipeline.by_stage[s].value) : ""}`} value={d.pipeline.by_stage[s].count} max={maxStage}
                    tone={s === "Vinta" ? "bg-success" : s === "Persa" ? "bg-danger" : "bg-brand"} />
                ))}
              </div>
              <div className="mt-3 text-sm text-slate-600">Aperte: <strong>{d.pipeline.open_total}</strong> · Valore: <strong>{eur(d.pipeline.open_value)}</strong></div>
            </div>
            <div className="ds-card p-4" data-testid="mgmt-quotes-analysis">
              <h3 className="ds-section-title mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-brand" /> Analisi preventivi</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Creati</div><div className="font-bold text-slate-800">{d.quotes_analysis.created}</div></div>
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Inviati</div><div className="font-bold text-slate-800">{d.quotes_analysis.sent}</div></div>
                <div className="rounded-lg bg-warning/10 p-3"><div className="text-xs text-slate-500">In attesa</div><div className="font-bold text-warning">{d.quotes_analysis.pending} · {eur(d.quotes_analysis.pending_value)}</div></div>
                <div className="rounded-lg bg-success/10 p-3"><div className="text-xs text-slate-500">Accettati</div><div className="font-bold text-success">{d.quotes_analysis.accepted} · {eur(d.quotes_analysis.accepted_value)}</div></div>
                <div className="rounded-lg bg-danger/10 p-3"><div className="text-xs text-slate-500">Persi</div><div className="font-bold text-danger">{d.quotes_analysis.rejected}</div></div>
                <div className="rounded-lg bg-brand/10 p-3"><div className="text-xs text-slate-500">Valore totale</div><div className="font-bold text-brand">{eur(d.quotes_analysis.total_value)}</div></div>
              </div>
            </div>
          </div>

          {/* RIGA 5 — sedi + operatori */}
          <div className="ds-card p-4" data-testid="mgmt-offices">
            <h3 className="ds-section-title mb-3 flex items-center gap-2"><Building2 className="h-4 w-4 text-brand" /> Andamento per sede</h3>
            <div className="ds-table-scroll">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="py-2">Sede</th><th className="py-2">Appunt.</th><th className="py-2">Opp. aperte</th><th className="py-2">Preventivi</th><th className="py-2">Accettati</th><th className="py-2">Valore prev.</th><th className="py-2">Conv.%</th><th className="py-2">Att. scadute</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {d.per_office.map((o) => (
                    <tr key={o.office_id} className="cursor-pointer hover:bg-brand/5" onClick={() => setOfficeId(o.office_id)} data-testid={`office-row-${o.office_id}`}>
                      <td className="py-2 font-medium text-slate-700">{o.office_name}</td><td className="py-2">{o.appointments}</td><td className="py-2">{o.opportunities_open}</td>
                      <td className="py-2">{o.quotes}</td><td className="py-2 text-success">{o.accepted}</td><td className="py-2">{eur(o.quote_value)}</td><td className="py-2">{o.conversion}%</td>
                      <td className={`py-2 ${o.overdue_activities > 0 ? "font-semibold text-danger" : ""}`}>{o.overdue_activities}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="ds-card p-4" data-testid="mgmt-operators">
            <h3 className="ds-section-title mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-brand" /> Andamento operatori</h3>
            <div className="ds-table-scroll">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    {[["operator_name", "Operatore"], ["appointments", "Appunt."], ["opportunities_open", "Opp."], ["quotes", "Preventivi"], ["accepted", "Accettati"], ["quote_value", "Valore"], ["conversion", "Conv.%"], ["overdue_activities", "Scadute"]].map(([c, l]) => (
                      <th key={c} className="cursor-pointer py-2 hover:text-brand" onClick={() => setSort(c)} data-testid={`op-sort-${c}`}>{l}{opSort.col === c ? (opSort.dir === "asc" ? " ↑" : " ↓") : ""}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedOps.length === 0 ? <tr><td colSpan={8} className="py-6 text-center text-slate-500">Nessun dato operatori nel periodo.</td></tr>
                    : sortedOps.map((o) => (
                      <tr key={o.operator_id} className="hover:bg-brand/5" data-testid={`op-row-${o.operator_id}`}>
                        <td className="py-2 font-medium text-slate-700">{o.operator_name}</td><td className="py-2">{o.appointments}</td><td className="py-2">{o.opportunities_open}</td>
                        <td className="py-2">{o.quotes}</td><td className="py-2 text-success">{o.accepted}</td><td className="py-2">{eur(o.quote_value)}</td><td className="py-2">{o.conversion}%</td>
                        <td className={`py-2 ${o.overdue_activities > 0 ? "font-semibold text-danger" : ""}`}>{o.overdue_activities}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* RIGA 6 — controllo attività */}
          <div className="ds-card p-4" data-testid="mgmt-activity-control">
            <h3 className="ds-section-title mb-3 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand" /> Controllo attività</h3>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Aperte</div><div className="font-bold text-slate-800">{d.activity_control.open}</div></div>
              <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Completate</div><div className="font-bold text-slate-800">{d.activity_control.completed}</div></div>
              <div className="rounded-lg bg-danger/10 p-3"><div className="text-xs text-slate-500">Scadute</div><div className="font-bold text-danger">{d.activity_control.overdue}</div></div>
              <div className="rounded-lg bg-warning/10 p-3"><div className="text-xs text-slate-500">Follow-up scaduti</div><div className="font-bold text-warning">{d.activity_control.followups_overdue}</div></div>
              <div className="rounded-lg bg-brand/10 p-3"><div className="text-xs text-slate-500">Appunt. futuri</div><div className="font-bold text-brand">{d.activity_control.appointments_future}</div></div>
              <button onClick={() => drill("/opportunita")} className="rounded-lg bg-warning/10 p-3 text-left hover:shadow-md" data-testid="mgmt-opp-no-activity">
                <div className="text-xs text-slate-500">Opp. senza attività</div><div className="font-bold text-warning">{d.activity_control.opportunities_no_activity}</div></button>
            </div>
          </div>

          {/* RIGA 7 — FASE 10: produzione / KO / target */}
          {d.phase10 && (
            <div className="ds-card p-4" data-testid="mgmt-phase10">
              <h3 className="ds-section-title mb-3 flex items-center gap-2"><Factory className="h-4 w-4 text-brand" /> Produzione</h3>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                <button onClick={() => drill("/produzione")} className="rounded-lg bg-brand/10 p-3 text-left hover:shadow-md" data-testid="mgmt-prod-count">
                  <div className="text-xs text-slate-500">Pratiche</div><div className="font-bold text-brand">{d.phase10.production_count}</div></button>
                <button onClick={() => drill("/produzione")} className="rounded-lg bg-brand/10 p-3 text-left hover:shadow-md" data-testid="mgmt-prod-value">
                  <div className="text-xs text-slate-500">Valore prod.</div><div className="font-bold text-brand">{eur(d.phase10.production_value)}</div></button>
                <button onClick={() => drill("/ko")} className="rounded-lg bg-danger/10 p-3 text-left hover:shadow-md" data-testid="mgmt-ko-count">
                  <div className="text-xs text-slate-500">KO</div><div className="font-bold text-danger">{d.phase10.ko_count}</div></button>
                <button onClick={() => drill("/ko")} className="rounded-lg bg-danger/10 p-3 text-left hover:shadow-md" data-testid="mgmt-ko-value">
                  <div className="text-xs text-slate-500">Valore KO</div><div className="font-bold text-danger">{eur(d.phase10.ko_value)}</div></button>
                <button onClick={() => drill("/target")} className="rounded-lg bg-brand/10 p-3 text-left hover:shadow-md" data-testid="mgmt-active-targets">
                  <div className="text-xs text-slate-500">Target attivi</div><div className="font-bold text-brand">{d.phase10.active_targets}</div></button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
